import type { CueWord } from "./cues";

const NO_SPACE_SCRIPT = /[\u0E00-\u0E7F\u0E80-\u0EFF\u1780-\u17FF\u3000-\u30FF\u4E00-\u9FFF]/u;

export interface WordTextSpan {
  /** UTF-16 offset of the word's first character in the cue text. */
  charStart: number;
  /** UTF-16 offset immediately after the word's last character. */
  charEnd: number;
}

/**
 * Walk an in-order word grid through its cue text. Whitespace may surround and
 * separate words, while whitespace inside a word remains part of that unit.
 * Returns null when the words are not a complete partition of the text.
 */
export function wordTextSpans(text: string, words: readonly CueWord[]): WordTextSpan[] | null {
  const spans: WordTextSpan[] = [];
  let cursor = 0;

  for (const word of words) {
    while (cursor < text.length && /\s/u.test(text[cursor] as string)) cursor += 1;
    if (!text.startsWith(word.text, cursor)) return null;
    const charEnd = cursor + word.text.length;
    spans.push({ charStart: cursor, charEnd });
    cursor = charEnd;
  }

  while (cursor < text.length && /\s/u.test(text[cursor] as string)) cursor += 1;
  return cursor === text.length ? spans : null;
}

const lastCharacter = (text: string): string => {
  const characters = Array.from(text);
  return characters[characters.length - 1] ?? "";
};
const firstCharacter = (text: string): string => Array.from(text)[0] ?? "";

export const containsNoSpaceScript = (text: string): boolean => NO_SPACE_SCRIPT.test(text);

/**
 * True when flattening would place whitespace between these two texts: every
 * junction is spaced except one between two no-space-script characters.
 */
export function hasSpaceJunction(leftText: string, rightText: string): boolean {
  return !(
    containsNoSpaceScript(lastCharacter(leftText)) &&
    containsNoSpaceScript(firstCharacter(rightText))
  );
}

/**
 * Join independently-owned cue text without inventing separators inside
 * no-space scripts. Existing surrounding whitespace is normalized away.
 */
export function joinCueTexts(leftText: string, rightText: string): string {
  const left = leftText.trim();
  const right = rightText.trim();
  if (!left) return right;
  if (!right) return left;

  return `${left}${hasSpaceJunction(left, right) ? " " : ""}${right}`;
}

/** Join word text with the same script-aware junction rule as cue merging. */
export function joinWordTexts(words: readonly CueWord[]): string {
  return words.reduce((text, word) => joinCueTexts(text, word.text), "");
}
